/**
 * Centralized enum definitions for the application.
 *
 * These are the names used across the Redux store, API translators, and UI
 * components. Keep values aligned with the backend — changing a string value
 * here is a breaking change.
 */

export enum CallResult {
  Success = 'SUCCESS',
  Failure = 'FAILURE',
  Error = 'ERROR',
}

export enum Role {
  Ops = 'Ops',
  PT = 'PT',
  Unknown = '_unknown_',
}

export enum AppointmentType {
  Ongoing = 'Ongoing',
  Assessment = 'Assessment',
  Personal = 'Personal',
}

export enum AppointmentStatus {
  Cancelled = 'Cancelled',
  Pending = 'Pending',
  TreatClient = 'Treat Client',
  Finished = 'Finished',
  Paid = 'Paid',
}

export enum AppointmentService {
  BcCignaHcpTreatment = 'BC / Cigna / HCP Treatment',
  BcCignaEval = 'BC / Cigna Eval',
  BcCignaTreatment = 'BC / Cigna Treatment',
  BcTeletherapy = 'BC Teletherapy',
  Blocked = 'Blocked',
  CancelledWithNotice = 'Cancelled with notice',
  CancelledWithoutNotice1st = 'Cancelled without notice 1st',
  CancelledWithoutNoticeMultiple = 'Cancelled without notice 2+',
  CashEval = 'Cash Eval',
  CashTeletherapy = 'Cash Teletherapy',
  CashTreatment = 'Cash Treatment',
  DischargeEval = 'Discharge Eval',
  EvalOnly = 'Eval Only',
  DoctorLunch = 'Doctor Lunch',
  HqMeeting = 'HQ Meeting',
  MedicareEVisit = 'Midicare E-Visit',
  MedicareEval = 'Medicare Eval',
  MedicareTeletherapyEval = 'Medicare Teletherapy Eval',
  MedicareTeletherapy = 'Medicare Teletherapy',
  MedicareTreatment = 'Medicare Treatment',
  OutOfOffice = 'Out of Office',
  PTO = 'PTO',
  PatientDoctorProgressVisit = 'Patient / Doctor Progress Visit',
  Personal = 'Personal',
  PrivateEval = 'Private Eval',
  PrivateTeletherapy = 'Private Teletherapy',
  PrivateTreatment = 'Private Treatment',
  Shadow = 'Shadow',
  TherapistMarketing = 'Therapist Marketing',
}

export enum PatientStatus {
  Active = 'Active',
  Deceased = 'Deceased',
  Inactive = 'Inactive',
}

export enum Gender {
  Male = 'M',
  Female = 'F',
}

export enum AppointmentStatusName {
  Arrived = 'Arrived',
  CancelledByPT = 'Cancelled by PT',
  CancelledFeeWaived = 'Cancelled fee waived',
  CancelledWithNotice = 'Cancelled with notice',
  CancelledWithoutNotice = 'Cancelled without notice',
  CancelledWithoutNotice1st = 'Cancelled without notice 1st',
  CancelledMoreWithoutNotice = 'Cancelled without notice 2+',
  CashTreatmentScheduled = 'Cash Treatment Scheduled',
  Finalized = 'Finalized',
  Finished = 'Finished',
  FlatRateEvalScheduled = 'Flat Rate Eval Scheduled',
  FlatRateTeletherapy = 'Flat Rate Teletherapy',
  FlatRateTreatmentScheduled = 'Flat Rate Treatment Scheduled',
  MedicareEvalScheduled = 'Medicare Eval Scheduled',
  MedicareTeletherapy = 'Medicare Teletherapy',
  MedicareTreatmentScheduled = 'Medicare Treatment Scheduled',
  NoShow = 'No Show',
  OnHold = 'On Hold',
  PrivateEvalScheduled = 'Private Eval Scheduled',
  PrivateTeletherapy = 'Private Teletherapy',
  PrivateTreatmentScheduled = 'Private Treatment Scheduled',
  ReScheduled = 'Re-Scheduled',
  Scheduled = 'Scheduled',
}

export enum CaseStatus {
  Active = 'Active',
  OnHold = 'On Hold',
  Discharged = 'Discharged',
}

export enum EmploymentType {
  FullTime = 'Full-Time',
  PartTime = 'Part-Time',
  None = 'None',
}

export enum LicenseType {
  Therapist = 'PT',
  TherapistAssistant = 'PTA',
  None = 'None',
}

export enum Status {
  Active = 'Active',
  Inactive = 'Inactive',
  None = 'None',
}

export enum FetchStatus {
  None = 'None',
  Fetching = 'Fetching',
  Success = 'Success',
  Failure = 'Failure',
  Unauthorized = 'Unauthorized',
}

export enum DoctorStatus {
  Active = 'Active',
  Inactive = 'Inactive',
}

// App-defined offer type, used to enforce types through discriminated union
export enum OfferType {
  ListOffer = 'ListOffer', // obtained through /offers endpoint
  DetailedOffer = 'DetailedOffer', // obtained through /offer/:offerId endpoint
  FullOffer = 'FullOffer', // the the same offer is fetched with both
}

export enum OfferStatus {
  Ready = 'Ready',
  Claimed = 'Claimed',
  Unclaimed = 'Unclaimed',
  Offered = 'Offered',
  Scheduled = 'Scheduled',
  PatientCancelled = 'PatientCancelled',
}

export enum CreditCardType {
  Visa = 'Visa',
  MasterCard = 'MasterCard',
  AmericanExpress = 'AmericanExpress',
  Discover = 'Discover',
}

export enum CardBrand {
  Amex = 'AMEX',
  Diners = 'DINERS',
  Discover = 'DISCOVER',
  Jcb = 'JCB',
  Mastercard = 'MASTERCARD',
  Unionpay = 'UNIONPAY',
  Visa = 'VISA',
  Unknown = 'UNKNOWN',
}

export enum Feature {
  Payment = 'PAYMENT',
  PaymentCardInput = 'PAYMENT_CARD_INPUT',
  PatientReferral = 'PATIENT_REFERRAL',
  OfferModel = 'OFFER_MODEL',
  FileUploadHook = 'FILE_UPLOAD_HOOK',
  CompositeImageUpload = 'COMPOSITE_IMAGE_UPLOAD',
  IntakeFormRequest = 'INTAKE_FORM_REQUEST',
  MissingDocumentsNotices = 'MISSING_DOCUMENTS_NOTICES',
  HomeHealthAlert = 'HOME_HEALTH_ALERT',
  NewEndSessionEndpoint = 'NEW_END_SESSION_ENDPOINT',
  OnHoldScheduleFilter = 'ON_HOLD_SCHEDULE_FILTER',
  DeveloperLogin = 'DEVELOPER_LOGIN',
  FutureAppointmentsOnly = 'FUTURE_APPOINTMENTS_ONLY',
  CalendarImport = 'CALENDAR_IMPORT',
}

export enum Cohort {
  None = 'NONE',
  Developers = 'Developers',
  Insiders = 'INSIDERS',
  FullTime = 'FULL_TIME',
}
